import type { Series, SeriesPoint } from '../api/types'
import { MISSING, NOT_SCHEDULED, OUT_OF_RANGE, SENTINEL, SPIKE, STALE } from './quality'
import { createSavedViewId } from './savedViews'
import { compileMonitoringExpression } from './monitoringExpressions'

export const ALARM_LAB_KEY = 'hydro.alarm-lab.v2'
/** Where version 1 kept its library: a reset level per condition, a reset
 * formula and one persistence time. It is read and migrated only while
 * nothing is saved under the current key, and it is never written, so a build
 * from before the reset was removed still finds its own library. */
export const ALARM_LAB_LEGACY_KEY = 'hydro.alarm-lab.v1'
export const ALARM_LAB_VERSION = 2
export const MAX_LAB_STRATEGIES = 40
/** Upper bound for M in "N of the last M readings". */
export const MAX_LAB_COUNT = 60
export type LabMetric = 'latest' | 'mean' | 'median' | 'rate' | 'absolute-rate'
export type LabLevel = 'warning' | 'alarm'
export type LabState = 'clear' | 'pending' | 'active' | 'unknown'
export type LabConfirmMode = 'first' | 'sustained' | 'count'
export type Truth = boolean | null
/** Per-condition (or, for a formula, per-channel) filter settings, in the
 * channel's own unit. They only apply while the strategy's filter is on. */
export interface LabInputFilter {
  spikeSize: number | null
  rangeMin: number | null
  rangeMax: number | null
}
export interface LabCondition extends LabInputFilter {
  id: string
  sourceId: string
  metric: LabMetric
  windowMinutes: number
  direction: 'above' | 'below'
  /** At least one level is set. Warning sits before alarm in the comparison's direction. */
  warning: number | null
  alarm: number | null
}
export interface LabChannelFilter extends LabInputFilter { sourceId: string }
/** All three settings are kept whichever mode is chosen, so switching modes in
 * the editor does not lose what the engineer typed. */
export interface LabConfirmation { mode: LabConfirmMode; minutes: number; count: number; of: number }
export interface LabFilter { spikes: boolean; neighbours: number; range: boolean }
export interface LabStrategy {
  id: string
  name: string
  combine: 'all' | 'any'
  conditions: LabCondition[]
  formula?: { trigger: string; channels: LabChannelFilter[] }
  confirm: LabConfirmation
  filter: LabFilter
  maxAgeMinutes: number
  quality: 'strict' | 'usable'
  /** Formula strategies only. Visual conditions carry their own levels. */
  severity: LabLevel
  note: string
  updatedAt: number
}
export interface LabSkip { time: number; value: number; reason: 'spike' | 'range' }
export interface LabConditionResult {
  id: string
  sourceId: string
  value: number | null
  unit: string
  /** The condition meets its lowest level (warning, or alarm when it has no warning level). */
  trigger: Truth
  /** The condition meets its alarm level. FALSE when it has none. */
  alarm: Truth
  reason: string
  points: number
  quality: number
  observedAt: number | null
  /** Readings the filter skipped in this evaluation, and the newest of them. */
  filtered: number
  lastFiltered: LabSkip | null
}
export interface LabFrame {
  time: number
  state: LabState
  /** Level of the open event, null when none is open. */
  level: LabLevel | null
  reason: string
  active: boolean
  event: number | null
  trigger: Truth
  alarm: Truth
  /** A new accepted reading arrived: the unit "N of the last M readings" counts. */
  sample: boolean
  conditions: LabConditionResult[]
  formula?: { trigger: Truth; reason: string }
}
export interface LabEvent {
  number: number
  opened: number
  closed: number | null
  peak: number | null
  unknownMinutes: number
  level: LabLevel
  highest: LabLevel
  /** Level changes inside the event, starting with the level it opened at. */
  levels: { time: number; level: LabLevel }[]
}
export interface LabInterval { from: number; to: number; state: LabState; level: LabLevel | null; firstFrame: number }
/** A reading the filter skipped when it arrived, for marking on the trace. */
export interface LabFilteredReading { input: number; time: number; value: number; reason: 'spike' | 'range' }
export interface LabReplay {
  frames: LabFrame[]
  events: LabEvent[]
  intervals: LabInterval[]
  filtered: LabFilteredReading[]
  from: number
  to: number
  activeMinutes: number
  unknownMinutes: number
  evaluated: number
  error: string | null
}

/** Interval ends are exclusive: inspecting a short interval must never land
 * on the following state, even if a caller retained an outdated frame index. */
export function labFrameInInterval(replay: Pick<LabReplay, 'frames'>, interval: LabInterval): number | null {
  const index = replay.frames.findIndex(frame => frame.time >= interval.from && frame.time < interval.to
    && frame.state === interval.state && frame.level === (interval.level ?? null))
  return index < 0 ? null : index
}

const MINUTE = 60_000
const DAY = 86_400_000
const finite = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v)
const optional = (v: unknown): v is number | null => v === null || finite(v)
const object = (v: unknown): v is Record<string, unknown> => v !== null && typeof v === 'object' && !Array.isArray(v)
const bounded = (v: unknown, n: number): v is string => typeof v === 'string' && v.length <= n
const sourceId = (v: unknown): v is string => bounded(v, 512) && /^[^|,\s]+\|[^|,\s]+$/.test(v)
const whole = (v: unknown, min: number, max: number): v is number => Number.isInteger(v) && (v as number) >= min && (v as number) <= max
type Store = Pick<Storage, 'getItem' | 'setItem'>
const NO_FILTER: LabInputFilter = { spikeSize: null, rangeMin: null, rangeMax: null }

export function createLabCondition(source = ''): LabCondition {
  return { id: createSavedViewId(), sourceId: source, metric: 'latest', windowMinutes: 60,
    direction: 'above', warning: null, alarm: 1, ...NO_FILTER }
}
export function createLabStrategy(source = ''): LabStrategy {
  return { id: createSavedViewId(), name: 'Untitled strategy', combine: 'all',
    conditions: [createLabCondition(source)], confirm: { mode: 'first', minutes: 30, count: 3, of: 5 },
    filter: { spikes: false, neighbours: 2, range: false }, maxAgeMinutes: 45,
    quality: 'strict', severity: 'alarm', note: '', updatedAt: Date.now() }
}

export function createLabFormulaStrategy(source = ''): LabStrategy {
  const strategy = createLabStrategy(source)
  return { ...strategy, name: 'Untitled formula rule', conditions: [],
    formula: { trigger: source ? `[${source}] >= 1` : '', channels: [] } }
}

/** Formula references are catalogue keys, never database queries or code. */
export function labStrategySources(strategy: LabStrategy): string[] {
  if (!strategy.formula) return [...new Set(strategy.conditions.map(c => c.sourceId))]
  try { return compileMonitoringExpression(strategy.formula.trigger).references }
  catch { return [] }
}

/** One filter entry per referenced channel, in reference order. Entries for
 * channels the formula no longer uses are dropped. */
export function labFormulaChannels(strategy: LabStrategy): LabChannelFilter[] {
  if (!strategy.formula) return []
  const stored = strategy.formula.channels
  return labStrategySources(strategy).map(ref => ({ ...NO_FILTER, ...stored.find(c => c.sourceId === ref), sourceId: ref }))
}

/** What the replay evaluates: the visual conditions, or one plain "latest
 * reading" input per formula reference carrying that channel's filter. */
export function labStrategyInputs(strategy: LabStrategy): LabCondition[] {
  if (!strategy.formula) return strategy.conditions
  return labFormulaChannels(strategy).map(f => ({ id: f.sourceId, sourceId: f.sourceId, metric: 'latest', windowMinutes: 60,
    direction: 'above', warning: null, alarm: null, spikeSize: f.spikeSize, rangeMin: f.rangeMin, rangeMax: f.rangeMax }))
}

/** Levels an event of this strategy can reach, lowest first. */
export function labStrategyLevels(strategy: LabStrategy): LabLevel[] {
  if (strategy.formula) return [strategy.severity]
  return (['warning', 'alarm'] as const).filter(level => strategy.conditions.some(c => c[level] !== null))
}

export function validateLabFormula(formula: Pick<NonNullable<LabStrategy['formula']>, 'trigger'>): string | null {
  try {
    const value = formula.trigger
    if (!bounded(value, 2000) || !value.trim()) return 'Enter a trigger formula of 1 to 2,000 characters.'
    const compiled = compileMonitoringExpression(value)
    if (compiled.resultType !== 'boolean') return 'The formula must return TRUE or FALSE. Use a comparison such as [station|channel] > 10.'
    if (!compiled.references.length) return 'The formula must reference at least one sensor channel.'
    for (const ref of compiled.references) {
      if (!sourceId(ref)) return `Use a channel reference such as [station|channel]. "${ref}" has no channel binding.`
    }
    if (compiled.references.length > 8) return 'Use no more than eight distinct channels in the formula.'
    return null
  } catch (error) { return error instanceof Error ? error.message : 'Invalid formula.' }
}

export function validateLabConfirmation(c: LabConfirmation): string | null {
  if (c.mode === 'sustained' && (!finite(c.minutes) || c.minutes < 1 || c.minutes > 1440)) return 'Sustained confirmation must be between 1 and 1,440 minutes.'
  if (c.mode === 'count' && (!whole(c.of, 1, MAX_LAB_COUNT) || !whole(c.count, 1, c.of))) {
    return `Use whole numbers for N of the last M readings, with N from 1 to M and M at most ${MAX_LAB_COUNT}.`
  }
  return ['first', 'sustained', 'count'].includes(c.mode) ? null : 'Choose how readings confirm an event.'
}

function inputFilterError(f: LabInputFilter, filter: LabFilter, name: string): string | null {
  if (![f.spikeSize, f.rangeMin, f.rangeMax].every(optional)) return `${name}: enter numbers for the filter settings.`
  if (filter.spikes && (f.spikeSize === null || f.spikeSize <= 0)) return `${name}: enter a spike size above 0.`
  if (filter.range && f.rangeMin === null && f.rangeMax === null) return `${name}: enter a plausible minimum, maximum or both.`
  if (filter.range && f.rangeMin !== null && f.rangeMax !== null && f.rangeMin >= f.rangeMax) return `${name}: the plausible minimum must be below the maximum.`
  return null
}

/** The longest gap a strategy may allow between readings: a day, or 31 days
 *  when it reads a manual sensor, read by hand weekly or monthly, which
 *  would otherwise be Unknown nearly all the time. The server holds the same
 *  limit (alerting/strategy.py max_age_limit). */
export function labMaxAgeLimit(s: LabStrategy): number {
  return labStrategySources(s).some(source => source.startsWith('manual|')) ? 44_640 : 1440
}

export function validateLabStrategy(s: LabStrategy): string | null {
  if (!s.name.trim() || s.name.length > 80) return 'Enter a strategy name of 1 to 80 characters.'
  const confirm = validateLabConfirmation(s.confirm)
  if (confirm) return confirm
  const limit = labMaxAgeLimit(s)
  if (!finite(s.maxAgeMinutes) || s.maxAgeMinutes < 1 || s.maxAgeMinutes > limit) return `Maximum reading gap must be between 1 and ${limit.toLocaleString('en-GB')} minutes.`
  if (!whole(s.filter.neighbours, 1, 5)) return 'Choose 1 to 5 neighbouring readings for the spike filter.'
  if (s.formula) {
    const formula = validateLabFormula(s.formula)
    if (formula) return formula
    for (const f of labFormulaChannels(s)) {
      const error = inputFilterError(f, s.filter, `[${f.sourceId}]`)
      if (error) return error
    }
    return null
  }
  if (!s.conditions.length || s.conditions.length > 4) return 'Choose between one and four conditions.'
  for (let i = 0; i < s.conditions.length; i++) {
    const c = s.conditions[i]!, name = `Condition ${i + 1}`
    if (!sourceId(c.sourceId)) return `Choose a channel for condition ${i + 1}.`
    if (!optional(c.warning) || !optional(c.alarm)) return `${name}: enter numeric levels.`
    if (c.warning === null && c.alarm === null) return `${name}: enter a warning level, an alarm level or both.`
    if (c.warning !== null && c.alarm !== null && (c.direction === 'above' ? c.warning >= c.alarm : c.warning <= c.alarm)) {
      return `${name}: the warning level must be ${c.direction === 'above' ? 'below' : 'above'} the alarm level.`
    }
    if (!finite(c.windowMinutes) || c.windowMinutes < 1 || c.windowMinutes > 1440) return `${name}: choose a window from 1 to 1,440 minutes.`
    if (c.metric === 'absolute-rate' && ((c.warning ?? 0) < 0 || (c.alarm ?? 0) < 0)) return 'Absolute rate levels cannot be negative.'
    const filter = inputFilterError(c, s.filter, name)
    if (filter) return filter
  }
  // With All, the alarm level needs every condition at its alarm level. A
  // condition without one would make the alarm level silently unreachable.
  if (s.combine === 'all' && s.conditions.some(c => c.alarm !== null) && s.conditions.some(c => c.alarm === null)) {
    return 'With All conditions, give every condition an alarm level, or none.'
  }
  return null
}

/** Version 1 strategies held a reset level beside each trigger level, a reset
 * formula and one persistence time. The reset is dropped, not reinterpreted:
 * an event now returns to normal when its condition is no longer met. The old
 * trigger becomes the level named by the old severity. */
export function migrateLabStrategy(v: unknown): unknown {
  if (!object(v) || 'confirm' in v) return v
  const hold = v.holdMinutes
  if (!finite(hold) || hold < 0 || hold > 1440) return null
  const level: LabLevel = v.severity === 'warning' ? 'warning' : 'alarm'
  const migrated: Record<string, unknown> = {
    id: v.id, name: v.name, combine: v.combine, quality: v.quality, severity: v.severity, note: v.note,
    maxAgeMinutes: v.maxAgeMinutes, updatedAt: v.updatedAt,
    // Version 1 accepted a persistence under a minute. Sustained starts at
    // one minute, and keeping the strategy beats dropping it on validation.
    confirm: hold > 0 ? { mode: 'sustained', minutes: Math.max(1, Math.round(hold)), count: 3, of: 5 } : { mode: 'first', minutes: 30, count: 3, of: 5 },
    filter: { spikes: false, neighbours: 2, range: false },
    conditions: Array.isArray(v.conditions) ? v.conditions.map(c => object(c) ? {
      id: c.id, sourceId: c.sourceId, metric: c.metric, windowMinutes: c.windowMinutes, direction: c.direction,
      warning: level === 'warning' ? c.threshold : null, alarm: level === 'alarm' ? c.threshold : null, ...NO_FILTER,
    } : c) : v.conditions,
  }
  if (object(v.formula)) migrated.formula = { trigger: v.formula.trigger, channels: [] }
  return migrated
}

function parseInputFilter(v: Record<string, unknown>): LabInputFilter | null {
  return optional(v.spikeSize) && optional(v.rangeMin) && optional(v.rangeMax)
    ? { spikeSize: v.spikeSize, rangeMin: v.rangeMin, rangeMax: v.rangeMax } : null
}

export function parseLabStrategy(v: unknown): LabStrategy | null {
  if (!object(v) || !bounded(v.id, 100) || !v.id || !bounded(v.name, 80)
    || !['all', 'any'].includes(String(v.combine)) || !['strict', 'usable'].includes(String(v.quality))
    || !['warning', 'alarm'].includes(String(v.severity)) || !bounded(v.note, 1200)
    || !finite(v.maxAgeMinutes) || !finite(v.updatedAt) || !object(v.confirm) || !object(v.filter)
    || !Array.isArray(v.conditions) || v.conditions.length > 4) return null
  const { confirm, filter } = v
  if (!['first', 'sustained', 'count'].includes(String(confirm.mode)) || !finite(confirm.minutes) || !finite(confirm.count) || !finite(confirm.of)
    || typeof filter.spikes !== 'boolean' || typeof filter.range !== 'boolean' || !finite(filter.neighbours)) return null
  let formula: LabStrategy['formula']
  if (v.formula !== undefined) {
    if (!object(v.formula) || !bounded(v.formula.trigger, 2000) || !Array.isArray(v.formula.channels) || v.formula.channels.length > 8) return null
    const channels: LabChannelFilter[] = []
    for (const c of v.formula.channels) {
      const limits = object(c) && sourceId(c.sourceId) ? parseInputFilter(c) : null
      if (!limits || channels.some(other => other.sourceId === c.sourceId)) return null
      channels.push({ sourceId: c.sourceId as string, ...limits })
    }
    formula = { trigger: v.formula.trigger, channels }
  }
  const conditions: LabCondition[] = []
  const ids = new Set<string>()
  for (const c of v.conditions) {
    const limits = object(c) ? parseInputFilter(c) : null
    if (!object(c) || !limits || !bounded(c.id, 100) || !c.id || ids.has(c.id) || !sourceId(c.sourceId)
      || !['latest', 'mean', 'median', 'rate', 'absolute-rate'].includes(String(c.metric))
      || !['above', 'below'].includes(String(c.direction)) || !finite(c.windowMinutes)
      || !optional(c.warning) || !optional(c.alarm)) return null
    ids.add(c.id)
    conditions.push({ id: c.id, sourceId: c.sourceId, metric: c.metric as LabMetric, windowMinutes: c.windowMinutes,
      direction: c.direction as LabCondition['direction'], warning: c.warning, alarm: c.alarm, ...limits })
  }
  const result: LabStrategy = { id: v.id, name: v.name.trim(), combine: v.combine as LabStrategy['combine'], conditions,
    confirm: { mode: confirm.mode as LabConfirmMode, minutes: confirm.minutes, count: confirm.count, of: confirm.of },
    filter: { spikes: filter.spikes, neighbours: filter.neighbours, range: filter.range },
    maxAgeMinutes: v.maxAgeMinutes, quality: v.quality as LabStrategy['quality'], severity: v.severity as LabLevel,
    note: v.note, updatedAt: v.updatedAt }
  if (formula) result.formula = formula
  return validateLabStrategy(result) ? null : result
}

export function readLabStrategies(demo = false, storage?: Store): LabStrategy[] {
  try {
    const store = storage ?? window.localStorage
    const current = store.getItem(ALARM_LAB_KEY)
    const raw = current ?? store.getItem(ALARM_LAB_LEGACY_KEY)
    if (raw !== null) {
      const parsed: unknown = JSON.parse(raw)
      const version = current !== null ? ALARM_LAB_VERSION : 1
      if (!object(parsed) || parsed.version !== version || !Array.isArray(parsed.strategies)) return []
      const entries: unknown[] = version === 1 ? parsed.strategies.map(migrateLabStrategy) : parsed.strategies
      const ids = new Set<string>()
      return entries.flatMap(v => {
        const s = parseLabStrategy(v)
        if (!s || ids.has(s.id) || ids.size >= MAX_LAB_STRATEGIES) return []
        ids.add(s.id); return [s]
      })
    }
  } catch { return demo ? demoLabStrategies() : [] }
  return demo ? demoLabStrategies() : []
}
export function writeLabStrategies(items: LabStrategy[], storage?: Store): boolean {
  if (items.length > MAX_LAB_STRATEGIES || items.some(s => !parseLabStrategy(s)) || new Set(items.map(s => s.id)).size !== items.length) return false
  try { (storage ?? window.localStorage).setItem(ALARM_LAB_KEY, JSON.stringify({ version: ALARM_LAB_VERSION, strategies: items })); return true }
  catch { return false }
}
export function demoLabStrategies(): LabStrategy[] {
  const level = createLabStrategy('rq-lajanuri|water_depth')
  level.id = 'demo-lab-depth'; level.name = 'Lajanuri · sustained high water'
  level.conditions[0] = { ...level.conditions[0]!, warning: 2.52, alarm: 2.62 }
  level.note = 'Demonstration limits for workflow exploration.'
  const compound = createLabStrategy('zhinvali|pz01_water_level')
  compound.id = 'demo-lab-compound'; compound.name = 'Zhinvali · combined piezometer response'
  compound.conditions[0] = { ...compound.conditions[0]!, warning: 775.75, alarm: 775.85 }
  compound.conditions.push({ ...createLabCondition('zhinvali|pz02_water_level'), warning: 772.15, alarm: 772.25 })
  compound.confirm = { ...compound.confirm, minutes: 60 }; compound.note = 'Demonstration limits, not engineering trigger levels.'
  const rate = createLabStrategy('rq-lajanuri|water_depth')
  rate.id = 'demo-lab-rate'; rate.name = 'Lajanuri · rapid water-level change'; rate.confirm = { ...rate.confirm, mode: 'first' }
  rate.conditions[0] = { ...rate.conditions[0]!, metric: 'absolute-rate', windowMinutes: 180, warning: 1.1, alarm: null }
  rate.note = 'Absolute rate evaluated over a three-hour window.'
  // Usable quality lets the fixture's flagged spike through, so the spike
  // filter rather than the quality policy is what keeps it from counting.
  const flow = createLabStrategy('rq-lajanuri|discharge')
  flow.id = 'demo-lab-flow'; flow.name = 'Lajanuri · high discharge, spike filtered'; flow.quality = 'usable'
  flow.conditions[0] = { ...flow.conditions[0]!, warning: 31, alarm: 32.5, spikeSize: 4, rangeMin: 0, rangeMax: 400 }
  flow.confirm = { mode: 'count', minutes: 30, count: 3, of: 5 }; flow.filter = { spikes: true, neighbours: 2, range: true }
  flow.note = 'Three of the last five readings must agree. Isolated spikes are skipped.'
  return [level, compound, rate, flow]
}

export function combineLabTruth(values: Truth[], mode: 'all' | 'any'): Truth {
  if (!values.length) return null
  if (mode === 'all') return values.includes(false) ? false : values.includes(null) ? null : true
  return values.includes(true) ? true : values.includes(null) ? null : false
}

/** Median of a non-empty list. */
export function labMedian(values: number[]): number {
  const sorted = [...values].sort((a, b) => a - b), middle = Math.floor(sorted.length / 2)
  return sorted.length % 2 ? sorted[middle]! : sorted[middle - 1]! / 2 + sorted[middle]! / 2
}

/** Whether values[index] is an isolated spike when only values[0..last] are
 * known. The window holds the reading itself and up to `neighbours` readings
 * on each side. The reading is part of the window, and the window is always
 * odd, because an even window of neighbours puts a genuine step's first
 * reading against a midpoint halfway between the two levels and skips it.
 * Near the newest reading the window slides back so it still holds 2K + 1
 * readings: a live system cannot see later readings, so a sudden change is
 * held back by up to K readings and accepted once K + 1 readings agree.
 * Until K later readings exist, a reading that continues a steady rise or
 * fall is not held back. On a ramp the earlier readings always sit below (or
 * above) the newest, so without this every reading of a rise would wait. */
export function isLabSpike(values: number[], index: number, last: number, neighbours: number, limit: number): boolean {
  const available = Math.min(last, values.length - 1) + 1
  let width = Math.min(2 * neighbours + 1, available)
  if (width % 2 === 0) width--
  const low = Math.max(0, index - width + 1), high = Math.min(index, available - width)
  const start = Math.min(high, Math.max(low, index - neighbours))
  if (Math.abs(values[index]! - labMedian(values.slice(start, start + width))) <= limit) return false
  return available - 1 - index >= neighbours || !continuesRun(values, index, neighbours, limit)
}

/** values[index] steps the same way as each of the K steps before it, and
 * by no more than the spike size away from their median step. A spike on a
 * ramp lands far from that prediction and is still held back. */
function continuesRun(values: number[], index: number, neighbours: number, limit: number): boolean {
  if (index < neighbours + 1) return false
  const steps: number[] = []
  for (let i = index - neighbours; i <= index; i++) steps.push(values[i]! - values[i - 1]!)
  const direction = Math.sign(steps[0]!)
  if (!direction || steps.some(step => Math.sign(step) !== direction)) return false
  return Math.abs(steps.at(-1)! - labMedian(steps.slice(0, -1))) <= limit
}

interface Reading { time: number; value: number | null; quality: number; ambiguous: boolean }
function indexReadings(points: SeriesPoint[]): Reading[] {
  const index = new Map<number, Reading>()
  for (const p of points) {
    const time = Date.parse(p[0])
    if (!Number.isFinite(time)) continue
    const prior = index.get(time)
    // Replay runs on a millisecond clock. Colliding instants are explicitly
    // unavailable, including sub-millisecond observations we cannot order.
    index.set(time, { time, value: prior ? null : p[1], quality: (prior?.quality ?? 0) | (p[2] ?? 0), ambiguous: !!prior })
  }
  return [...index.values()].sort((a, b) => a.time - b.time)
}
function beforeIndex(points: Reading[], time: number): number {
  let low = 0, high = points.length
  while (low < high) { const mid = Math.floor((low + high) / 2); if (points[mid]!.time <= time) low = mid + 1; else high = mid }
  return low - 1
}

const ELIGIBLE = 0, INVALID = 1, RANGE = 2
/** One evaluated input with its filter decisions worked out once. Range and
 * quality decisions never change. A spike decision depends on how many later
 * readings were known, so it is kept for each count from 0 to 2K. */
interface Input {
  condition: LabCondition
  series: Series | undefined
  readings: Reading[]
  kind: Uint8Array
  rank: Int32Array
  lastEligible: Int32Array
  spikes: Uint8Array[] | null
}
function prepareInput(c: LabCondition, strategy: LabStrategy, series: Series | undefined, readings: Reading[]): Input {
  const mask = MISSING | SENTINEL | NOT_SCHEDULED | (strategy.quality === 'strict' ? OUT_OF_RANGE | SPIKE | STALE : 0)
  const kind = new Uint8Array(readings.length), rank = new Int32Array(readings.length), lastEligible = new Int32Array(readings.length)
  const values: number[] = []
  readings.forEach((p, i) => {
    const valid = !p.ambiguous && p.value !== null && Number.isFinite(p.value) && !(p.quality & mask)
    const outside = valid && strategy.filter.range && ((c.rangeMin !== null && p.value! < c.rangeMin) || (c.rangeMax !== null && p.value! > c.rangeMax))
    kind[i] = !valid ? INVALID : outside ? RANGE : ELIGIBLE
    rank[i] = kind[i] === ELIGIBLE ? values.push(p.value!) - 1 : -1
    lastEligible[i] = values.length - 1
  })
  let spikes: Uint8Array[] | null = null
  if (strategy.filter.spikes && c.spikeSize !== null && c.spikeSize > 0) {
    const k = strategy.filter.neighbours
    spikes = Array.from({ length: 2 * k + 1 }, (_, later) => {
      const flags = new Uint8Array(values.length)
      for (let e = 0; e < values.length; e++) flags[e] = isLabSpike(values, e, e + later, k, c.spikeSize!) ? 1 : 0
      return flags
    })
  }
  return { condition: c, series, readings, kind, rank, lastEligible, spikes }
}

function evaluateInput(input: Input, strategy: LabStrategy, time: number): LabConditionResult {
  const { condition: c, series, readings, kind, rank, spikes } = input
  const rate = c.metric === 'rate' || c.metric === 'absolute-rate'
  const result: LabConditionResult = { id: c.id, sourceId: c.sourceId, value: null, unit: (series?.unit ?? '') + (rate ? '/day' : ''),
    trigger: null, alarm: null, reason: '', points: 0, quality: 0, observedAt: null, filtered: 0, lastFiltered: null }
  const unknown = (reason: string) => ({ ...result, reason })
  if (!series) return unknown('Channel unavailable.')
  if (series.kind !== 'instant') return unknown('This tool evaluates instantaneous quantities only.')
  const newest = beforeIndex(readings, time)
  if (newest < 0) return unknown('No reading at or before this time.')
  const maxGap = strategy.maxAgeMinutes * MINUTE
  if (time - readings[newest]!.time > maxGap) return unknown('The latest reading exceeds the maximum reading gap.')
  // Spike decisions use only readings known at this replay time. Those for
  // the newest K readings are provisional: the filter is holding them back
  // until K later readings show whether they are a spike or a change.
  const known = input.lastEligible[newest]!
  const skipped = (i: number) => kind[i] === RANGE || (kind[i] === ELIGIBLE && !!spikes && spikes[Math.min(spikes.length - 1, known - rank[i]!)]![rank[i]!] === 1)
  const held = (i: number) => kind[i] === ELIGIBLE && known - rank[i]! < strategy.filter.neighbours
  const note = (i: number) => {
    result.filtered++
    if (!result.lastFiltered || readings[i]!.time > result.lastFiltered.time) result.lastFiltered = { time: readings[i]!.time, value: readings[i]!.value!, reason: kind[i] === RANGE ? 'range' : 'spike' }
  }
  const explain = (p: Reading) => p.ambiguous ? 'Duplicate or unresolved timestamp.' : 'A required reading is missing or excluded by quality policy.'
  // A skipped reading counts as absent: the newest accepted reading stands in
  // for it. A reading outside the plausible range or a confirmed spike shows
  // nothing, so the gap runs on past it. A reading still held back does show
  // the channel reporting, so the gap is measured from it: holding back at
  // most K readings must not turn the input Unknown.
  let used = newest, reach = time
  while (used >= 0 && reach - readings[used]!.time <= maxGap && skipped(used)) {
    if (held(used)) reach = readings[used]!.time
    note(used); used--
  }
  if (used < 0 || reach - readings[used]!.time > maxGap) return unknown('Newer readings were filtered and no accepted reading lies within the maximum reading gap.')
  const last = readings[used]!
  if (kind[used] === INVALID) return unknown(explain(last))
  let metric = last.value!
  result.points = 1; result.quality = last.quality; result.observedAt = last.time
  if (c.metric !== 'latest') {
    const start = last.time - c.windowMinutes * MINUTE
    let anchor = beforeIndex(readings, start)
    while (anchor >= 0 && start - readings[anchor]!.time <= maxGap && skipped(anchor)) anchor--
    const first = readings[anchor]
    if (!first || start - first.time > maxGap) return unknown('Insufficient history at the start of the metric window.')
    if (kind[anchor] === INVALID) return unknown(explain(first))
    if (first.time === last.time) return unknown('At least two distinct observations are required.')
    const included: number[] = []
    let reached = first.time, points = 1, quality = first.quality
    for (let i = anchor + 1; i <= used; i++) {
      const p = readings[i]!
      if (kind[i] === INVALID) return unknown(explain(p))
      const skip = skipped(i)
      // As above, a held-back reading bridges a gap but adds no value.
      if (skip && !held(i)) { note(i); continue }
      if (p.time - reached > maxGap) return unknown('A data gap interrupts the metric window.')
      reached = p.time
      if (skip) { note(i); continue }
      points++; quality |= p.quality
      if (p.time > start) included.push(p.value!)
    }
    result.points = points; result.quality = quality
    if (c.metric === 'mean' || c.metric === 'median') {
      // Statistics of observed samples in (start, end]. The left-hand anchor
      // verifies coverage but does not bias the sample.
      metric = c.metric === 'mean' ? included.reduce((n, v) => n + v / included.length, 0) : labMedian(included)
      result.points = included.length
    } else {
      metric = (last.value! - first.value!) / ((last.time - first.time) / DAY)
      if (c.metric === 'absolute-rate') metric = Math.abs(metric)
    }
  }
  if (!Number.isFinite(metric)) return unknown('The metric is outside the supported numeric range.')
  const meets = (level: number | null) => level !== null && (c.direction === 'above' ? metric >= level : metric <= level)
  return { ...result, value: metric, trigger: meets(c.warning ?? c.alarm), alarm: meets(c.alarm),
    reason: ({ latest: 'Latest accepted reading', mean: 'Sample mean over a covered window', median: 'Sample median over a covered window',
      rate: 'Change divided by actual elapsed days', 'absolute-rate': 'Change divided by actual elapsed days' })[c.metric] }
}

/** Confirmation of one level. Each level is confirmed with the same rule, so
 * an escalation to Alarm is held to the same standard as opening the event.
 * "N of the last M" keeps one window of results per condition (one for a
 * formula), so each channel is counted at its own reading rate. */
interface Tracker { confirmed: boolean; since: number | null; through: number; windows: boolean[][] }
const tracker = (windows: number): Tracker => ({ confirmed: false, since: null, through: -Infinity, windows: Array.from({ length: windows }, () => []) })
function record(t: Tracker, input: number, truth: boolean, of: number) {
  const window = t.windows[input]!
  window.push(truth)
  if (window.length > of) window.shift()
}
const met = (t: Tracker, input: number) => t.windows[input]!.filter(Boolean).length
/** Count mode. Only known results are recorded, and a verdict that depends on
 * unavailable data neither opens nor clears. */
function settle(t: Tracker, verdict: Truth, known: boolean) {
  if (verdict === true) t.confirmed = true
  else if (verdict === false && known) t.confirmed = false
}
function advance(t: Tracker, confirm: LabConfirmation, truth: Truth, known: boolean, through: number, time: number) {
  if (t.confirmed) {
    // No reset level: FALSE with every input available returns it to normal.
    if (truth === false && known) { t.confirmed = false; t.since = null }
    return
  }
  if (truth !== true) { t.since = null; return }
  t.since ??= time; t.through = through
  if (confirm.mode === 'first' || through - t.since >= confirm.minutes * MINUTE) t.confirmed = true
}

/** Independent, read-only historical evaluation. It cannot create or clear
 * production events. Unknown data never opens or closes an event. */
export function replayLabStrategy(strategy: LabStrategy, series: Series[], from: number, to: number, truncated = false): LabReplay {
  const result: LabReplay = { frames: [], events: [], intervals: [], filtered: [], from, to, activeMinutes: 0, unknownMinutes: 0, evaluated: 0, error: null }
  const invalid = validateLabStrategy(strategy)
  if (invalid || !Number.isFinite(from) || !Number.isFinite(to) || from >= to || to - from > 32 * DAY || truncated) {
    return { ...result, error: truncated ? 'The response is incomplete. Choose a shorter replay period.' : invalid ?? 'Choose a valid replay period of at most 32 days.' }
  }
  const byId = new Map(series.map(s => [s.station + '|' + s.parameter, s]))
  const formula = strategy.formula ? compileMonitoringExpression(strategy.formula.trigger) : null
  const indexed = new Map(labStrategySources(strategy).map(ref => [ref, indexReadings(byId.get(ref)?.points ?? [])]))
  const inputs = labStrategyInputs(strategy).map(c => prepareInput(c, strategy, byId.get(c.sourceId), indexed.get(c.sourceId) ?? []))
  inputs.forEach((input, i) => input.readings.forEach((p, r) => {
    if (p.time < from || p.time > to || input.kind[r] === INVALID) return
    // As the trace shows it: the decision made when the reading arrived.
    const spike = input.kind[r] === ELIGIBLE && input.spikes?.[0]![input.rank[r]!] === 1
    if (input.kind[r] === RANGE || spike) result.filtered.push({ input: i, time: p.time, value: p.value!, reason: spike ? 'spike' : 'range' })
  }))
  const times = new Set([from, to])
  for (const points of indexed.values()) for (const p of points) {
    if (p.time >= from && p.time <= to) times.add(p.time)
    const expires = p.time + strategy.maxAgeMinutes * MINUTE + 1
    if (expires > from && expires < to) times.add(expires)
  }
  // At least a one-minute resolution for dwell expiry, bounded to 46k steps.
  // No interpolated observations are created; every step uses as-of readings.
  for (let t = from + MINUTE; t < to; t += MINUTE) times.add(t)
  const { confirm } = strategy
  const hasWarning = labStrategyLevels(strategy).includes('warning'), hasAlarm = labStrategyLevels(strategy).includes('alarm')
  const low = tracker(formula ? 1 : inputs.length), high = tracker(formula ? 1 : inputs.length)
  // The newest reading seen per input, and in count mode the one last counted.
  const seen = inputs.map(() => -Infinity), counted = inputs.map(() => -Infinity)
  let active: LabEvent | null = null
  let previous: LabFrame | undefined
  for (const time of [...times].sort((a, b) => a - b)) {
    const conditions = inputs.map(input => evaluateInput(input, strategy, time))
    let unavailable = conditions.some(c => c.value === null)
    let trigger: Truth, alarm: Truth, formulaResult: LabFrame['formula']
    if (formula) {
      const value = unavailable ? null : formula.evaluate(Object.fromEntries(conditions.map(c => [c.sourceId, c.value])))
      trigger = typeof value === 'boolean' ? value : null
      unavailable ||= trigger === null
      alarm = strategy.severity === 'alarm' ? trigger : false
      formulaResult = { trigger, reason: unavailable ? 'A referenced channel or formula result is unavailable.' : 'The formula evaluated with accepted source readings.' }
    } else {
      trigger = combineLabTruth(conditions.map(c => c.trigger), strategy.combine)
      alarm = hasAlarm ? combineLabTruth(conditions.map(c => c.alarm), strategy.combine) : false
    }
    let sample = conditions.some((c, i) => c.observedAt !== null && c.observedAt > seen[i]!)
    conditions.forEach((c, i) => { if (c.observedAt !== null) seen[i] = Math.max(seen[i]!, c.observedAt) })
    if (confirm.mode === 'count') {
      sample = false
      if (formula) {
        // One result per round of readings, so a fast channel cannot repeat a
        // slow channel's reading into several counted results.
        if (trigger !== null && conditions.every((c, i) => c.observedAt !== null && c.observedAt > counted[i]!)) {
          conditions.forEach((c, i) => { counted[i] = c.observedAt! })
          record(low, 0, trigger, confirm.of); record(high, 0, alarm === true, confirm.of); sample = true
        }
      } else conditions.forEach((c, i) => {
        // Each condition counts only its own new readings. Unknown results
        // are not counted either way.
        if (c.observedAt === null || c.observedAt <= counted[i]!) return
        counted[i] = c.observedAt
        if (c.trigger === null) return
        record(low, i, c.trigger, confirm.of); record(high, i, c.alarm === true, confirm.of); sample = true
      })
      // A condition with unavailable data has no verdict, so its earlier count
      // cannot open the event, and All and Any combine as everywhere else.
      const verdict = (t: Tracker): Truth => formula ? (unavailable ? null : met(t, 0) >= confirm.count)
        : combineLabTruth(conditions.map((c, i) => c.value === null ? null : met(t, i) >= confirm.count), strategy.combine)
      settle(low, verdict(low), !unavailable)
      settle(high, verdict(high), !unavailable)
    } else {
      // Sustained confirmation needs confirming observations, not wall time: All
      // and formulas wait for every input, Any for the newest confirming one.
      const through = (key: 'trigger' | 'alarm') => {
        const observed = (formula ? conditions : conditions.filter(c => c[key] === true)).map(c => c.observedAt ?? -Infinity)
        return formula || strategy.combine === 'all' ? Math.min(...observed) : Math.max(...observed)
      }
      advance(low, confirm, trigger, !unavailable, through('trigger'), time)
      advance(high, confirm, alarm, !unavailable, through('alarm'), time)
    }
    const open = low.confirmed || high.confirmed
    const level: LabLevel = formula ? strategy.severity : hasAlarm && high.confirmed ? 'alarm' : hasWarning ? 'warning' : 'alarm'
    let state: LabState, reason: string
    if (open) {
      const opening = !active, changed = !!active && active.level !== level
      if (!active) {
        active = { number: result.events.length + 1, opened: time, closed: null, peak: null, unknownMinutes: 0, level, highest: level, levels: [{ time, level }] }
        result.events.push(active)
      } else if (changed) {
        active.level = level; active.levels.push({ time, level })
        if (level === 'alarm') active.highest = 'alarm'
      }
      if (unavailable && trigger !== true) {
        state = 'unknown'; reason = 'Data unavailable. The open event is kept until valid readings show the condition is no longer met.'
      } else {
        state = 'active'
        reason = opening ? `${confirmText(strategy, level === 'alarm' ? high : low)} ${labLevelLabel(level)} event opened.`
          : changed ? (level === 'alarm' ? `${confirmText(strategy, high)} The event escalated to Alarm.` : 'The alarm level is no longer confirmed. The event returned to Warning.')
          : confirm.mode === 'count' ? `${countText(strategy, low)} The event remains open.`
          : 'The condition remains met.'
        if (!changed && !opening && level === 'warning' && hasAlarm && pending(strategy, high)) reason += ` Alarm level: ${pendingText(strategy, high)}`
      }
    } else if (active) {
      active.closed = time; active = null
      state = pending(strategy, low) ? 'pending' : 'clear'
      reason = confirm.mode !== 'count' ? 'The condition is no longer met. The replay event returned to normal.'
        : low.windows.length > 1 ? `${countText(strategy, low)} The replay event returned to normal.`
        : `Fewer than ${confirm.count} of the last ${confirm.of} readings meet the condition. The replay event returned to normal.`
    } else if (unavailable && trigger !== true) {
      state = 'unknown'; reason = confirm.mode === 'count' ? 'Required data is unavailable. Unknown readings are not counted.'
        : confirm.mode === 'sustained' ? 'Required data is unavailable. Confirmation restarts after recovery.' : 'Required data is unavailable.'
    } else if (pending(strategy, low)) {
      state = 'pending'; reason = pendingText(strategy, low)
    } else {
      state = 'clear'; reason = 'The condition is not met.'
    }
    if (formulaResult && unavailable) reason = `${formulaResult.reason} ${active ? 'The open event is kept.' : confirm.mode === 'sustained' ? 'Confirmation restarts after recovery.'
      : confirm.mode === 'count' ? 'Unknown readings are not counted.' : 'No event opens until it recovers.'}`
    if (active && !formula && conditions[0]?.value !== null && conditions[0]?.value !== undefined) {
      const value = conditions[0].value
      active.peak = active.peak === null ? value : strategy.conditions[0]!.direction === 'above' ? Math.max(active.peak, value) : Math.min(active.peak, value)
    }
    const frame: LabFrame = { time, state, level: active?.level ?? null, reason, active: !!active, event: active?.number ?? null,
      trigger, alarm, sample, conditions, ...(formulaResult ? { formula: formulaResult } : {}) }
    if (previous) {
      const duration = (time - previous.time) / MINUTE
      if (previous.state === 'active') result.activeMinutes += duration
      if (previous.state === 'unknown') { result.unknownMinutes += duration; if (previous.event) result.events[previous.event - 1]!.unknownMinutes += duration }
    }
    if (state !== 'unknown') result.evaluated++
    result.frames.push(frame); previous = frame
  }
  result.frames.forEach((frame, i) => {
    const end = result.frames[i + 1]?.time ?? to
    if (frame.time === end) return
    const prior = result.intervals.at(-1)
    if (prior?.state === frame.state && prior.level === frame.level) prior.to = end
    else result.intervals.push({ from: frame.time, to: end, state: frame.state, level: frame.level, firstFrame: i })
  })
  return result
}

/** How far "N of the last M" has got: All needs every condition's count, Any one. */
function progress(s: LabStrategy, t: Tracker): number {
  const counts = t.windows.map((_, i) => met(t, i))
  return s.combine === 'all' ? Math.min(...counts) : Math.max(...counts)
}
function countText(s: LabStrategy, t: Tracker): string {
  const counts = t.windows.map((_, i) => met(t, i))
  if (counts.length === 1) return `${counts[0]} of the last ${s.confirm.of} readings meet the condition.`
  const parts = counts.map((n, i) => `${n} for condition ${i + 1}`)
  return `Readings meeting each condition, of its last ${s.confirm.of}: ${parts.slice(0, -1).join(', ')} and ${parts.at(-1)}.`
}
function pending(s: LabStrategy, t: Tracker): boolean {
  return !t.confirmed && (s.confirm.mode === 'count' ? progress(s, t) > 0 : s.confirm.mode === 'sustained' && t.since !== null)
}
function pendingText(s: LabStrategy, t: Tracker): string {
  const { confirm } = s
  if (confirm.mode !== 'count') return `Confirmed readings cover ${Math.max(0, Math.floor((t.through - (t.since ?? t.through)) / MINUTE))} of ${confirm.minutes} minutes.`
  const each = t.windows.length === 1 ? '' : s.combine === 'all' ? ' for every condition' : ' for one condition'
  return `${countText(s, t)} ${confirm.count} are needed${each}.`
}
function confirmText(s: LabStrategy, t: Tracker): string {
  return s.confirm.mode === 'count' ? countText(s, t)
    : s.confirm.mode === 'sustained' ? `The condition held for ${s.confirm.minutes} minutes.` : 'The condition is met.'
}

export function labMetricLabel(metric: LabMetric): string {
  return ({ latest: 'Latest value', mean: 'Window mean', median: 'Window median', rate: 'Signed rate', 'absolute-rate': 'Absolute rate' })[metric]
}
export function labLevelLabel(level: LabLevel): string { return level === 'alarm' ? 'Alarm' : 'Warning' }
export function labConfirmationSummary(c: LabConfirmation): string {
  return c.mode === 'first' ? 'First reading' : c.mode === 'sustained' ? `Sustained for ${c.minutes} min` : `${c.count} of the last ${c.of} readings`
}
export function labConditionSummary(c: LabCondition, label: string, unit: string): string {
  const u = `${unit}${c.metric.includes('rate') ? '/day' : ''}`.trim()
  const op = c.direction === 'above' ? '≥' : '≤'
  const levels = (['warning', 'alarm'] as const).flatMap(level => c[level] === null ? [] : [`${op} ${c[level]}${u ? ' ' + u : ''} ${level}`])
  return `${label}: ${labMetricLabel(c.metric).toLowerCase()} ${levels.join(', ')}`.trim()
}
